// 🏃⚡ INSTANT POG RUN — tap a deal, pay, courier launches THAT SECOND.
// No merchant bugging, no runner wait. Opens via window event "airpog:instant-run".
import { useEffect, useState } from "react";
import axios from "axios";
import { X, Zap, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { id: "preordered", label: "📞 I pre-ordered — courier just picks it up" },
  { id: "handoff", label: "🧾 Courier shows my POG receipt at the counter" },
];

export const InstantRunSheet = ({ userPos }) => {
  const [biz, setBiz] = useState(null);
  const [menu, setMenu] = useState(null);
  const [cat, setCat] = useState("goods");
  const [mode, setMode] = useState("preordered");
  const [note, setNote] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const open = (e) => { setBiz(e.detail?.b || null); setQuote(null); setErr(""); setCat("goods"); setNote(""); };
    window.addEventListener("airpog:instant-run", open);
    return () => window.removeEventListener("airpog:instant-run", open);
  }, []);
  useEffect(() => {
    if (biz) axios.get(`${BACKEND_URL}/api/instant/menu`).then((r) => setMenu(r.data)).catch(() => {});
  }, [biz]);

  if (!biz) return null;
  const cats = menu?.categories || [];
  const sel = cats.find((c) => c.id === cat);
  const hasPos = !!(userPos && biz.lat != null);
  const rideLink = hasPos
    ? `https://m.uber.com/ul/?action=setPickup&pickup[latitude]=${userPos.lat}&pickup[longitude]=${userPos.lng}&dropoff[latitude]=${biz.lat}&dropoff[longitude]=${biz.lng}&dropoff[nickname]=${encodeURIComponent(biz.name || "POG stop")}`
    : null;
  const merchantLink = biz.links?.web || null;

  const authMsg = (e) => (e?.response?.status === 401 ? "Sign in first to POG RUN IT! 🏃" : e?.response?.data?.detail || "That didn't work — try again!");
  const getQuote = async () => {
    if (!hasPos) { setErr("We need your location to price the run — enable GPS!"); return; }
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${BACKEND_URL}/api/instant/quote`,
        { pickup_lat: biz.lat, pickup_lng: biz.lng, drop_lat: userPos.lat, drop_lng: userPos.lng },
        { withCredentials: true });
      setQuote(r.data);
    } catch (e) { setErr(authMsg(e)); }
    setBusy(false);
  };
  const pay = async () => {
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${BACKEND_URL}/api/instant/checkout`, {
        business_id: biz.id, business_name: biz.name || "", category: cat, pickup_mode: mode,
        note, quoted_total_cents: quote?.total_cents || 0,
        pickup_lat: biz.lat, pickup_lng: biz.lng, drop_lat: userPos.lat, drop_lng: userPos.lng,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.assign(r.data.url);
    } catch (e) {
      if (e?.response?.status === 409) setQuote(null);
      setErr(authMsg(e)); setBusy(false);
    }
  };

  return (
    <div data-testid="instant-run-sheet" className="fixed inset-0 z-[2200] flex items-end justify-center">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => setBiz(null)} />
      <div className="relative w-full max-w-md bg-slate-900 border-t-2 border-emerald-400/60 rounded-t-3xl p-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[85vh] overflow-y-auto">
        <button data-testid="instant-run-close" onClick={() => setBiz(null)}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center">
          <X className="w-4 h-4 text-slate-300" />
        </button>
        <p className="text-[10px] font-black tracking-[0.2em] text-emerald-300 flex items-center gap-1">
          <Zap className="w-3.5 h-3.5" /> INSTANT POG RUN
        </p>
        <h2 className="font-heading font-extrabold text-lg text-white mt-1 leading-tight">{biz.name}</h2>
        <p className="text-[11px] text-slate-400 font-bold">
          {biz.distance_miles != null ? `${biz.distance_miles} mi away · ` : ""}pay → courier dispatched that second. No calls, no waiting.
        </p>

        <div className="flex flex-wrap gap-1.5 mt-3">
          {cats.map((c) => (
            <button key={c.id} data-testid={`instant-cat-${c.id}`} onClick={() => { setCat(c.id); setErr(""); }}
              className={`rounded-full px-2.5 py-1 text-[11px] font-heading font-extrabold transition-colors ${
                cat === c.id ? "bg-emerald-400 text-slate-900" : c.available || c.mode === "ride_link"
                  ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-slate-800/60 text-slate-500"}`}>
              {c.emoji} {c.label}
            </button>
          ))}
        </div>
        {sel && <p data-testid="instant-cat-note" className="text-[10px] font-bold text-slate-400 mt-1.5">{sel.note}</p>}

        {sel?.mode === "courier" ? (
          <>
            <div className="mt-3 space-y-1.5">
              {MODES.map((m) => (
                <button key={m.id} data-testid={`instant-mode-${m.id}`} onClick={() => setMode(m.id)}
                  className={`w-full text-left rounded-xl px-3 py-2 text-[12px] font-heading font-bold border transition-colors ${
                    mode === m.id ? "bg-emerald-400/15 border-emerald-400/60 text-emerald-200" : "bg-slate-800/60 border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                  {m.label}
                </button>
              ))}
            </div>
            <input data-testid="instant-note-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
              placeholder="What's the order? (name it's under, deal to grab…)"
              className="w-full mt-2.5 rounded-xl bg-slate-800 border border-slate-700 px-3 py-2.5 text-[13px] text-white placeholder:text-slate-500 outline-none focus:border-emerald-400/60" />
            {quote ? (
              <div data-testid="instant-quote-box" className="mt-3 rounded-xl bg-slate-800/80 border border-slate-700 p-3 text-[12px] font-bold text-slate-300 space-y-0.5">
                <p>🚗 Courier{quote.estimate ? " (estimate)" : ""}: <span className="text-white">${(quote.courier_cents / 100).toFixed(2)}</span></p>
                <p>🏃 POG dispatch fee: <span className="text-white">${(quote.service_fee_cents / 100).toFixed(2)}</span></p>
                <p className="text-emerald-300 text-sm font-black pt-1">TOTAL {quote.price}</p>
              </div>
            ) : null}
            <button data-testid={quote ? "instant-pay-btn" : "instant-quote-btn"} disabled={busy}
              onClick={quote ? pay : getQuote}
              className="w-full mt-3 rounded-2xl bg-emerald-400 hover:bg-emerald-300 disabled:opacity-60 text-slate-900 font-heading font-black text-sm py-3 flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {quote ? `PAY ${quote.price} & DISPATCH ⚡` : "PRICE THIS RUN"}
            </button>
          </>
        ) : sel?.mode === "ride_link" ? (
          <a data-testid="instant-ride-link" href={rideLink || "#"} target="_blank" rel="noopener noreferrer"
            className={`block w-full mt-3 rounded-2xl text-center font-heading font-black text-sm py-3 ${rideLink ? "bg-white text-slate-900 hover:bg-slate-200" : "bg-slate-800 text-slate-500 pointer-events-none"}`}>
            🧍 RIDE THERE WITH UBER ↗
          </a>
        ) : sel?.mode === "merchant_link" ? (
          merchantLink ? (
            <a data-testid="instant-merchant-link" href={merchantLink} target="_blank" rel="noopener noreferrer"
              className="block w-full mt-3 rounded-2xl bg-white text-slate-900 hover:bg-slate-200 text-center font-heading font-black text-sm py-3">
              🌐 ORDER DIRECT FROM {(biz.name || "MERCHANT").toUpperCase().slice(0, 18)} ↗
            </a>
          ) : (
            <p data-testid="instant-vaulted-note" className="mt-3 text-[12px] font-bold text-slate-500 text-center">⚰️ Vaulted — no courier carries this. Visit the merchant in person.</p>
          )
        ) : sel && !sel.available ? (
          <p data-testid="instant-locked-note" className="mt-3 text-[12px] font-bold text-amber-300/80 text-center">🔐 Coming online with production keys — stay tuned, POG'R!</p>
        ) : null}

        {err && <p data-testid="instant-run-error" className="mt-2 text-[12px] font-bold text-rose-400 text-center">{err}</p>}
      </div>
    </div>
  );
};
